import { useSearchParams } from 'react-router';
import { useData } from '../../../contexts/data-context/use-data';
import { ErrorSection } from '../../../design-system/error-section/error-section';
import { LoadingSection } from '../../../design-system/loading/loading';
import { PaymentsFlowModal } from '../components/payments-flow-modal';
import { usePaymentFlow } from '../use-payment-flow';
import { PaymentsSection } from './payments-section';

const VIEW_ALL_RECIPIENTS_PARAM = 'view';

export const PaymentsPageWrapper = () => {
  const {
    paymentAction: paymentFlowVariantWithStep,
    updatePaymentAction,
    updateSelectedBeneficiaryId,
    selectedBeneficiaryId,
    clearPaymentState,
  } = usePaymentFlow();
  const { data: entityData, isLoading: isLoadingEntityData, error: entityDataError } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const isViewAllRecipients = searchParams.get(VIEW_ALL_RECIPIENTS_PARAM) === 'all';

  if (isLoadingEntityData) {
    return <LoadingSection text="Loading data..." />;
  }

  if (!entityData) {
    return <ErrorSection error={entityDataError} />;
  }

  const { companies, accounts, beneficiaries, evidence: evidences } = entityData;

  const handleSelectBeneficiary = (beneficiaryId: string) => {
    updatePaymentAction('EXTERNAL_PAYMENT');
    updateSelectedBeneficiaryId(beneficiaryId);
  };

  const onViewAllRecipients = () => {
    searchParams.set('view', 'all');
    setSearchParams(searchParams);
  };

  const viewAllRecipientsConfig = isViewAllRecipients
    ? {
        visible: true as const,
        onGoBack: () => {
          searchParams.delete('view');
          setSearchParams(searchParams);
        },
      }
    : {
        visible: false as const,
      };

  return (
    <>
      <PaymentsSection
        beneficiaries={beneficiaries}
        viewAllRecipientsConfig={viewAllRecipientsConfig}
        onInitiateBillPay={() => updatePaymentAction('BILL_PAY_UPLOAD_INVOICE')}
        onInitiateInternalTransfer={() => updatePaymentAction('INTERNAL_PAYMENT')}
        onInitiateExternalTransfer={() => {
          if (beneficiaries.length === 0) {
            updatePaymentAction('CREATE_BENEFICIARY');
            return;
          }

          updatePaymentAction('VIEW_ALL_BENEFICIARIES');
        }}
        onInitiateBeneficiaryPayment={handleSelectBeneficiary}
        onViewAllRecipients={onViewAllRecipients}
      />

      {paymentFlowVariantWithStep && (
        <PaymentsFlowModal
          isOpen={!!paymentFlowVariantWithStep}
          onClose={() => {
            clearPaymentState();
          }}
          paymentAction={paymentFlowVariantWithStep}
          beneficiaries={beneficiaries}
          evidences={evidences}
          selectedBeneficiaryId={selectedBeneficiaryId}
          accounts={accounts}
          companyData={{
            // All accounts link to the same company, hence picking the first one
            id: companies[0].id,
            country: companies[0].legalEntity.country,
          }}
          onSetPaymentAction={updatePaymentAction}
          onSelectBeneficiary={handleSelectBeneficiary}
          // TODO: explore if overflow-auto can be moved to the modal content component.
          // Right now it has overflow-clip, but need proper testing to make a change there to avoid impact on other modals, especially the entity ones.
          // To be done in a separate PR.
          className="h-full w-full overflow-auto bg-muted"
        />
      )}
    </>
  );
};
